// ViralScoreCard.jsx — compact 5-metric scoreboard rendered above the post
// grid on the Promote tab. Each metric is a 0–100 score with a coloured
// bar; the "Overall" pill carries the batch verdict. Updated atomically
// after the auto-regenerate loop picks the best-scoring batch.

import React from "react";
import { Flame, Sparkles, Activity, Anchor, Target, Trophy, RotateCw } from "lucide-react";

// Score → tailwind color band. Mirrors the 85+ "viral" threshold.
function bandColor(score) {
  if (score >= 85) return "from-emerald-500 to-cyan-400";
  if (score >= 70) return "from-amber-500 to-orange-400";
  return "from-rose-500 to-fuchsia-500";
}

function bandText(score) {
  if (score >= 85) return "text-emerald-300";
  if (score >= 70) return "text-amber-300";
  return "text-rose-300";
}

const Metric = ({ label, icon: Icon, value, testid }) => {
  const v = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-white/10 bg-zinc-950/70 p-3 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
          <Icon className="h-3 w-3" strokeWidth={2.2} />
          {label}
        </div>
        <span className={`font-mono text-[14px] font-bold ${bandText(v)}`}>{v}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${bandColor(v)} transition-all duration-500`}
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
};

export const ViralScoreCard = ({ scores, batchOverall, attempts = 1, testid = "viral-score-card" }) => {
  if (!scores || !scores.length) return null;

  // Per-batch averages across the 4 posts.
  const avg = (key) =>
    Math.round(scores.reduce((s, p) => s + (Number(p?.[key]) || 0), 0) / scores.length);
  const hook    = avg("hook_strength");
  const scroll  = avg("scroll_stopping_potential");
  const trend   = avg("trend_match");
  const cta     = avg("cta_strength");
  const overall = Number(batchOverall) || avg("overall");

  return (
    <section
      data-testid={testid}
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900 p-5 sm:p-6 mb-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">
            <Trophy className="h-3 w-3" strokeWidth={2.5} />
            Viral score
          </div>
          <h3 className="font-display text-lg font-black text-white mt-1.5 tracking-tight">
            Batch performance forecast
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {attempts > 1 && (
            <div
              data-testid={`${testid}-attempts`}
              className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-violet-200"
              title="Auto-regenerated to find the highest scoring batch"
            >
              <RotateCw className="h-3 w-3" strokeWidth={2.4} />
              {attempts}× tries · kept best
            </div>
          )}
          <div
            data-testid={`${testid}-overall`}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${
              overall >= 85
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : overall >= 70
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                  : "border-rose-500/40 bg-rose-500/10 text-rose-200"
            }`}
          >
            <Flame className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span className="font-mono text-[16px] font-bold tracking-tight">{overall}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">/100</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Metric label="Hook Strength"             icon={Sparkles} value={hook}   testid={`${testid}-hook`} />
        <Metric label="Scroll-Stopping Potential" icon={Activity} value={scroll} testid={`${testid}-scroll`} />
        <Metric label="Trend Match"               icon={Anchor}   value={trend}  testid={`${testid}-trend`} />
        <Metric label="CTA Strength"              icon={Target}   value={cta}    testid={`${testid}-cta`} />
      </div>
    </section>
  );
};

export default ViralScoreCard;
